import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Col,
  Row,
  Container,
  CardBody,
  Button,
  Label,
  Badge,
  Toast,
  Input,
  FormGroup,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import { FaCheck, FaTimes } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "components/BreadCrumb/Breadcrumbs";
import { showAlert } from "components/Common/commonFunctions";
import { bankInfoDetails } from "actions/publisher";
import { updateBillingInfoStatus } from "actions/publisher";

const BankDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const notificationAlertRef = useRef(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("PENDING");
  const [bankDetails, setBankDetails] = useState(null);
  const [error, setError] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Get the row data from navigation state
  const rowData = location.state;

  // Get user ID from localStorage (assuming you store it)
  const userData = JSON.parse(localStorage.getItem("p") || "{}");
  const currentUserId = userData?.role; // Fallback to 1 if not found

  //==> notify function for notifications
  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  //==> getStatusBadge function
  const getStatusBadge = (status) => {
    const statusMap = {
      APPROVED: { color: "success", label: "Approved" },
      PENDING: { color: "warning", label: "Pending" },
      REJECTED: { color: "danger", label: "Rejected" },
      INACTIVE: { color: "secondary", label: "Inactive" },
      ACTIVE: { color: "primary", label: "Active" },
      Approved: { color: "success", label: "Approved" },
      Pending: { color: "warning", label: "Pending" },
      Rejected: { color: "danger", label: "Rejected" },
      Inactive: { color: "secondary", label: "Inactive" },
    };
    const s = statusMap[status] || statusMap.Pending;
    return <Badge color={s.color} className="px-3 py-2">{s.label}</Badge>;
  };

  //==> Fetch bank details by ID
  const fetchBankDetails = async (id) => {
    setShowLoader(true);
    setError(null);
    try {
      const res = await bankInfoDetails(id);
      if (res?.code === 200 && res?.data) {
        const data = res.data;

        const mappedData = {
          id: data.id,
          legal_entity_name: data.legalName || "-",
          publisherId: data.publisherId || "-",
          recipient_name: data.accountName || data.recipientName || "-",
          country: data.countryName || "-",
          billing_address: data.billingAddress || "-",
          trn_vat_id: data.vatNumber || "-",
          payment_currency: data.paymentCurrency || "-",
          bank_name: data.bankName || "-",
          recipient_address: data.recipientAddress || "-",
          bank_account: data.accountNumber || data.bankAccount || "-",
          bank_address: data.bankAddress || "-",
          iban: data.iban || "-",
          swift_bic: data.swiftCode || data.swiftBic || "-",
          routing_number: data.routingNumber || "-",
          ifsc_code: data.ifscCode || "-",
          invoice_contact_email: data.invoiceEmail || "-",
          payment_terms: data.paymentTerms || "-",
          minimum_payout: data.minimumPayout || 0,
          tax_residency: data.taxResidency || "-",
          status: data.approvalStatus || data.status || "PENDING",
          approval_status: data.approvalStatus || "PENDING",
          created_at: data.createdAt || "-",
          updated_at: data.updatedAt || "-",
          approved_by: data.approvedBy || "-",
          approved_at: data.approvedAt || "-",
          notes: "Awaiting admin approval",
        };

        setBankDetails(mappedData);
        setCurrentStatus(mappedData.approval_status || "PENDING");
      } else {
        setError(res?.message || "Failed to fetch bank details");
        notify("danger", "Failed to fetch bank details");
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      setError(error?.message || "Something went wrong");
      notify("danger", "Something went wrong while fetching bank details");
      setTimeout(() => {
        navigate("/admin/publisher-bank-details");
      }, 2000);
    } finally {
      setShowLoader(false);
    }
  };

  //==> Update approval status
  const updateApprovalStatus = async (status, reason = null) => {
    if (!bankDetails) return;

    try {
      setShowLoader(true);
      const payload = {
        approvalStatus: status,
        approvedBy: currentUserId,
        approvedAt: new Date().toISOString(),
        publisherID: bankDetails.publisherId,
        rejectReason: reason,
      };
      const res = await updateBillingInfoStatus(bankDetails.id, payload);
      if (res?.code === 200) {
        setCurrentStatus(status);
        setBankDetails({
          ...bankDetails,
          approval_status: status,
          approved_by: currentUserId,
          approved_at: new Date().toISOString(),
        });

        const message =
          status === "APPROVED"
            ? `Bank details for "${bankDetails.legal_entity_name}" approved successfully!`
            : `Bank details for "${bankDetails.legal_entity_name}" rejected!`;

        notify("success", message);

        // Reset rejection reason after successful update
        setRejectReason("");
        setShowRejectModal(false);
      } else {
        const errorMsg = res?.message || `Failed to ${status.toLowerCase()} bank details`;
        notify("danger", errorMsg);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      notify("danger", "Something went wrong while updating status");
    } finally {
      setShowLoader(false);
    }
  };

  //==> handleApprove function with confirmation popup
  const handleApprove = () => {
    if (!bankDetails) return;

    showAlert({
      title: "Approve Bank Details",
      message: `Are you sure you want to approve the bank details for "${bankDetails.legal_entity_name}"?`,
      type: "success",
      confirmText: "Yes, Approve",
      cancelText: "Cancel",
      onConfirm: () => {
        updateApprovalStatus("APPROVED");
      },
      onCancel: () => {},
    });
  };

  //==> handleReject function with custom modal
  const handleReject = () => {
    if (!bankDetails) return;
    // Reset rejection reason when opening modal
    setRejectReason("");
    setShowRejectModal(true);
  };

  //==> Confirm reject function
  const confirmReject = () => {
    if (!rejectReason.trim()) {
      setError("Rejection reason is required");
      return;
    }
    setError(null);
    updateApprovalStatus("REJECTED", rejectReason);
  };

  //==> Render Field Component - Label on top, Value below
  const Field = ({ label, value }) => (
    <div className="mb-3">
      <Label className="text-muted small mb-1" style={{ fontSize: "12px", fontWeight: "600" }}>
        {label}
      </Label>
      <div className="font-weight" style={{ fontSize: "14px", color: "#2c3e50" }}>
        {value || "-"}
      </div>
    </div>
  );

  // Fetch data when component mounts or when rowData changes
  useEffect(() => {
    if (rowData && rowData.id) {
      fetchBankDetails(rowData.id);
    } else {
   
      setTimeout(() => {
        navigate("/admin/publisher-bank-details");
      }, 2000);
    }
  }, [rowData]);

  // If no data, show loading or error state
  if (!bankDetails && !showLoader) {
    return (
      <>
        <div className="rna-container">
          <NotificationAlert ref={notificationAlertRef} />
        </div>
        <Container fluid className="mt--6">
          <Card className="advertiser-card">
            <CardBody>
              <div className="text-center py-5">
                <h5>No bank details found</h5>
              </div>
            </CardBody>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <div className="action-form-container">
        <Container fluid className="mt--6">
          <Card className="advertiser-card">
            <Breadcrumbs />
            <CardBody>
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h4 className="advertiser-title mb-0">
                    Publisher Billing Information (PIF)
                  </h4>
                  <small className="text-muted">
                    Review and verify publisher bank details
                  </small>
                </div>
                <div className="d-flex align-items-center gap-3">
                  {bankDetails && (
                    <div>
                      <span className="text-muted mr-2">Status:</span>
                      {getStatusBadge(currentStatus)}
                    </div>
                  )}
                </div>
              </div>

              {showLoader && (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: "100px" }}
                >
                  <i
                    className="fa fa-spinner fa-spin"
                    style={{ fontSize: "25px" }}
                  />
                </div>
              )}

              {!showLoader && bankDetails && (
                <>
                  {/* All Fields in Two Columns */}
                  <Row>
                    {/* Left Column */}
                    <Col md={6}>
                      <Field label="Legal Entity Name" value={bankDetails.legal_entity_name} />
                      <Field label="Bank Name" value={bankDetails.bank_name} />
                      <Field label="Country" value={bankDetails.country} />
                      <Field label="TRN/VAT ID" value={bankDetails.trn_vat_id} />
                      <Field label="Currency" value={bankDetails.payment_currency} />
                      <Field label="Payment Terms" value={bankDetails.payment_terms} />
                      <Field
                        label="Minimum Payout"
                        value={bankDetails.minimum_payout ? `$${bankDetails.minimum_payout}` : "-"}
                      />
                      <Field label="Bank Account" value={bankDetails.bank_account} />
                    </Col>

                    {/* Right Column */}
                    <Col md={6}>
                      <Field label="Billing Address" value={bankDetails.billing_address} />
                      <Field label="IBAN" value={bankDetails.iban} />
                      <Field label="IFSC Code" value={bankDetails.ifsc_code} />
                      <Field label="SWIFT/BIC" value={bankDetails.swift_bic} />
                      <Field label="Invoice Email" value={bankDetails.invoice_contact_email} />
                      <Field label="Tax Residency" value={bankDetails.tax_residency} />
                      <Field
                        label={currentStatus === "REJECTED" ? "Rejected By" : "Approved By"}
                        value={
                          bankDetails.approved_by === "SUPER_ADMIN"
                            ? "Super Admin"
                            : bankDetails.approved_by || "-"
                        }
                      />
                      <Field label="Approved At" value={bankDetails.approved_at || "-"} />
                    </Col>
                  </Row>

                  {/* Action Buttons */}
                  {currentStatus === "PENDING" && (
                    <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
                      <Button
                        color="danger"
                        className="px-4 btn-gradient-primary"
                        onClick={handleReject}
                        disabled={showLoader}
                      >
                        <FaTimes className="mr-2" />
                        Reject
                      </Button>
                      <Button
                        color="success"
                        className="px-4"
                        onClick={handleApprove}
                        disabled={showLoader}
                      >
                        <FaCheck className="mr-2" />
                        Approve
                      </Button>
                    </div>
                  )}

                  {currentStatus !== "PENDING" && (
                    <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                      <span className="text-muted">
                        This bank detail has been {currentStatus?.toLowerCase()}
                      </span>
                    </div>
                  )}
                </>
              )}

              {error && (
                <div className="text-center text-danger py-3">
                  <p>{error}</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Custom Reject Modal */}
      <div
        className={`modal fade ${showRejectModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowRejectModal(false);
            setRejectReason("");
            setError(null);
          }
        }}
      >
        <div className="modal-dialog modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Reject Bank Details</h5>
              <button
                type="button"
                className="close"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason("");
                  setError(null);
                }}
              >
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to reject the bank details for "
                <strong>{bankDetails?.legal_entity_name}</strong>"?
              </p>
              <FormGroup className="mt-3">
                <Label for="rejectReason" className="font-weight-bold">
                  Rejection Reason <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="rejectReason"
                  name="rejectReason"
                  placeholder="Please enter the reason for rejection..."
                  value={rejectReason}
                  onChange={(e) => {
                    setRejectReason(e.target.value);
                    setError(null);
                  }}
                  rows="4"
                  className={error ? "is-invalid" : ""}
                  autoFocus
                />
                {error && <div className="text-danger small mt-1">{error}</div>}
              </FormGroup>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                  className="btn btn-gradient-primary"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason("");
                  setError(null);
                }}
              >
                Cancel
              </button>
              <Button
                type="button"
              color="secondary"
               
                onClick={confirmReject}
                disabled={showLoader}
              >
                {showLoader ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  "Yes, Reject"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BankDetails;